import { computed, inject, ref } from 'vue';

import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import { CommandBus } from 'src/util/command/CommandBus';
import { canAutoOpenSetupWizard } from 'src/util/platform/browserInteractionCapabilities';

const isWizardOpenRef = ref(false);
const isDeferredRef = ref(false);

/**
 * Reads the current viewport width, defaulting to 0 outside a browser.
 *
 * A width of 0 fails the auto-open capability check, so a missing window never
 * persists a user-wide completion by accident.
 */
const resolveViewportWidth = (): number => (typeof window === 'undefined' ? 0 : window.innerWidth);

export function useSetupWizard() {
	const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
	const commandBus = inject<CommandBus | undefined>('commandBus', undefined);

	const isCompleted = computed<boolean>(() => userInterfaceSettingsStore.setupWizardCompleted);
	const shouldShow = computed<boolean>(() => !isCompleted.value);
	const isOpen = computed<boolean>(() => isWizardOpenRef.value);
	const isDeferred = computed<boolean>(() => isDeferredRef.value);

	function open(): void {
		isDeferredRef.value = false;
		isWizardOpenRef.value = true;
	}

	function close(): void {
		isWizardOpenRef.value = false;
	}

	function defer(): void {
		isDeferredRef.value = true;
		close();
	}

	function complete(): void {
		// Nur Geraete, die alle Schritte zeigen koennen, duerfen den benutzerweiten
		// Abschluss setzen. Sonst verbraucht ein Handy-Durchlauf den Desktop-Start.
		if (canAutoOpenSetupWizard(resolveViewportWidth())) {
			userInterfaceSettingsStore.markSetupWizardCompleted({ commandBus });
		}

		isDeferredRef.value = true;
		close();
	}

	function openIfNeeded(): void {
		if (shouldShow.value) {
			open();
		}
	}

	return {
		close,
		complete,
		defer,
		isCompleted,
		isDeferred,
		isOpen,
		open,
		openIfNeeded,
		shouldShow,
	};
}
